"use client"

import { useState } from "react"
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  Grid,
} from "@mui/material"
import { Formik, FieldArray } from "formik"
import * as yup from "yup"
import useMediaQuery from "@mui/material/useMediaQuery"
import { AdminPanelSettings, Security, Person } from "@mui/icons-material"
import EnhancedHeader from "../../components/EnhancedHeader"
import ProfessionalCard from "../../components/ProfessionalCard"
import StyledButton from "../../components/StyledButton"
import { createAdmin } from "../../data/ApiController.js"

const AdminForm = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)")
  const [submitStatus, setSubmitStatus] = useState(null)

  const checkoutSchema = yup.object().shape({
    name: yup.string().required("Username is required"),
    password: yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    address: yup.string().required("Address is required"),
    phoneNumber: yup.string().required("Phone number is required"),
    adminType: yup.string().required("Admin Type is required"),
  })

  const initialValues = {
    name: "",
    password: "",
    email: "",
    address: "",
    phoneNumber: "",
    adminType: "",
    roles: [],
    permissions: [],
  }

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      const adminData = {
        ...values,
        roles: values.adminType === "Static Admin" ? values.roles : [],
        permissions: values.adminType === "Dynamic Admin" ? values.permissions : [],
      }
      await createAdmin(adminData)
      setSubmitStatus({ type: "success", message: "Admin created successfully!" })
      resetForm()
    } catch (error) {
      setSubmitStatus({ type: "error", message: "Failed to create admin. Please try again." })
    }
  }

  const staticRoles = [
    { value: "Service", label: "Service Management", description: "Manage service offerings and configurations" },
    { value: "Material", label: "Materials Management", description: "Handle material inventory and pricing" },
    { value: "Labor", label: "Labor Rate Management", description: "Set and update labor rates across regions" },
  ]

  const dynamicPermissions = [
    { value: "inventory-admin", label: "Inventory Admin", description: "Manage inventory and stock levels" },
    { value: "customer-support", label: "Customer Support", description: "Handle customer inquiries and support" },
    { value: "support-admin", label: "Support Admin", description: "Administrative support functions" },
    { value: "marketing", label: "Marketing", description: "Marketing campaigns and content management" },
    { value: "web-editor", label: "Web Editor", description: "Website content and page management" },
  ]

  return (
    <Box sx={{ padding: "20px", backgroundColor: "transparent", minHeight: "100vh" }}>
      <EnhancedHeader
        title="Create New Admin"
        subtitle="Add a new administrator to the platform with specific roles and permissions"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Admin Management", href: "/admin-list" },
          { label: "Create Admin", current: true },
        ]}
      />

      {submitStatus && (
        <Alert severity={submitStatus.type} sx={{ mb: 3 }} onClose={() => setSubmitStatus(null)}>
          {submitStatus.message}
        </Alert>
      )}

      <Formik onSubmit={handleFormSubmit} initialValues={initialValues} validationSchema={checkoutSchema}>
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12} lg={8}>
                <ProfessionalCard>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Person sx={{ mr: 2, color: "#96D232" }} />
                      <Typography variant="h5" fontWeight={600}>
                        Basic Information
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="text"
                          label="Full Name"
                          name="name"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.name}
                          error={!!touched.name && !!errors.name}
                          helperText={touched.name && errors.name}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": { borderColor: "#96D232" },
                              "&.Mui-focused fieldset": { borderColor: "#96D232" },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="email"
                          label="Email Address"
                          name="email"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.email}
                          error={!!touched.email && !!errors.email}
                          helperText={touched.email && errors.email}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": { borderColor: "#96D232" },
                              "&.Mui-focused fieldset": { borderColor: "#96D232" },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="tel"
                          label="Phone Number"
                          name="phoneNumber"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.phoneNumber}
                          error={!!touched.phoneNumber && !!errors.phoneNumber}
                          helperText={touched.phoneNumber && errors.phoneNumber}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": { borderColor: "#96D232" },
                              "&.Mui-focused fieldset": { borderColor: "#96D232" },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="password"
                          label="Password"
                          name="password"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.password}
                          error={!!touched.password && !!errors.password}
                          helperText={touched.password && errors.password}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": { borderColor: "#96D232" },
                              "&.Mui-focused fieldset": { borderColor: "#96D232" },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="text"
                          label="Address"
                          name="address"
                          multiline
                          rows={2}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.address}
                          error={!!touched.address && !!errors.address}
                          helperText={touched.address && errors.address}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": { borderColor: "#96D232" },
                              "&.Mui-focused fieldset": { borderColor: "#96D232" },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </ProfessionalCard>
              </Grid>

              {/* Admin Type Selection */}
              <Grid item xs={12} lg={4}>
                <ProfessionalCard>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <AdminPanelSettings sx={{ mr: 2, color: "#96D232" }} />
                      <Typography variant="h5" fontWeight={600}>
                        Admin Type
                      </Typography>
                    </Box>

                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Select Admin Type</InputLabel>
                      <Select
                        name="adminType"
                        value={values.adminType}
                        onChange={(e) => {
                          handleChange(e)
                          setFieldValue("roles", [])
                          setFieldValue("permissions", [])
                        }}
                        label="Select Admin Type"
                        sx={{
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#96D232" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#96D232" },
                        }}
                      >
                        <MenuItem value="Static Admin">
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              Static Admin
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Core system management
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="Dynamic Admin">
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              Dynamic Admin
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Operational management
                            </Typography>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </CardContent>
                </ProfessionalCard>
              </Grid>

              {/* Permissions */}
              {values.adminType && (
                <Grid item xs={12}>
                  <ProfessionalCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        <Security sx={{ mr: 2, color: "#96D232" }} />
                        <Typography variant="h5" fontWeight={600}>
                          {values.adminType === "Static Admin" ? "System Roles" : "Operational Permissions"}
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        {values.adminType === "Static Admin"
                          ? "Select the core system management roles for this admin"
                          : "Choose the operational areas this admin can manage"}
                      </Typography>

                      {values.adminType === "Static Admin" && (
                        <FieldArray name="roles">
                          {() => (
                            <Grid container spacing={2}>
                              {staticRoles.map((role) => (
                                <Grid item xs={12} sm={6} md={4} key={role.value}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                      border: values.roles.includes(role.value)
                                        ? "2px solid #96D232"
                                        : "1px solid #e0e0e0",
                                      backgroundColor: values.roles.includes(role.value)
                                        ? "rgba(150, 210, 50, 0.1)"
                                        : "transparent",
                                      "&:hover": {
                                        borderColor: "#96D232",
                                        backgroundColor: "rgba(150, 210, 50, 0.05)",
                                      },
                                    }}
                                    onClick={() => {
                                      if (values.roles.includes(role.value)) {
                                        setFieldValue(
                                          "roles",
                                          values.roles.filter((r) => r !== role.value),
                                        )
                                      } else {
                                        setFieldValue("roles", [...values.roles, role.value])
                                      }
                                    }}
                                  >
                                    <CardContent sx={{ p: 2 }}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={values.roles.includes(role.value)}
                                            sx={{
                                              color: "#96D232",
                                              "&.Mui-checked": { color: "#96D232" },
                                            }}
                                          />
                                        }
                                        label={
                                          <Box>
                                            <Typography variant="body1" fontWeight={500}>
                                              {role.label}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                              {role.description}
                                            </Typography>
                                          </Box>
                                        }
                                        sx={{ m: 0, width: "100%" }}
                                      />
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </FieldArray>
                      )}

                      {values.adminType === "Dynamic Admin" && (
                        <FieldArray name="permissions">
                          {() => (
                            <Grid container spacing={2}>
                              {dynamicPermissions.map((permission) => (
                                <Grid item xs={12} sm={6} md={4} key={permission.value}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                      border: values.permissions.includes(permission.value)
                                        ? "2px solid #96D232"
                                        : "1px solid #e0e0e0",
                                      backgroundColor: values.permissions.includes(permission.value)
                                        ? "rgba(150, 210, 50, 0.1)"
                                        : "transparent",
                                      "&:hover": {
                                        borderColor: "#96D232",
                                        backgroundColor: "rgba(150, 210, 50, 0.05)",
                                      },
                                    }}
                                    onClick={() => {
                                      if (values.permissions.includes(permission.value)) {
                                        setFieldValue(
                                          "permissions",
                                          values.permissions.filter((p) => p !== permission.value),
                                        )
                                      } else {
                                        setFieldValue("permissions", [...values.permissions, permission.value])
                                      }
                                    }}
                                  >
                                    <CardContent sx={{ p: 2 }}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={values.permissions.includes(permission.value)}
                                            sx={{
                                              color: "#96D232",
                                              "&.Mui-checked": { color: "#96D232" },
                                            }}
                                          />
                                        }
                                        label={
                                          <Box>
                                            <Typography variant="body1" fontWeight={500}>
                                              {permission.label}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                              {permission.description}
                                            </Typography>
                                          </Box>
                                        }
                                        sx={{ m: 0, width: "100%" }}
                                      />
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </FieldArray>
                      )}
                    </CardContent>
                  </ProfessionalCard>
                </Grid>
              )}

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <StyledButton type="submit" size="large" disabled={isSubmitting} sx={{ px: 6, py: 1.5 }}>
                    {isSubmitting ? "Creating Admin..." : "Create Admin Account"}
                  </StyledButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Box>
  )
}

export default AdminForm
